/**
 * Simple logger utility for development and debugging
 * Provides namespaced logging with different levels
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private namespace: string;
  private enabled: boolean = __DEV__ ?? true;

  constructor(namespace: string) {
    this.namespace = namespace;
  }

  private log(level: LogLevel, ...args: any[]) {
    if (!this.enabled) return;

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${this.namespace}]`;

    switch (level) {
      case 'debug':
        console.log(`${prefix} 🔍`, ...args);
        break;
      case 'info':
        console.info(`${prefix} ℹ️`, ...args);
        break;
      case 'warn':
        console.warn(`${prefix} ⚠️`, ...args);
        break;
      case 'error':
        console.error(`${prefix} ❌`, ...args);
        break;
    }
  }

  debug(...args: any[]) {
    this.log('debug', ...args);
  }

  info(...args: any[]) {
    this.log('info', ...args);
  }

  warn(...args: any[]) {
    this.log('warn', ...args);
  }

  error(...args: any[]) {
    this.log('error', ...args);
  }
}

/**
 * Create a namespaced logger
 * @param namespace - The namespace for this logger (e.g., 'ErrorHandler', 'Auth')
 */
export const createLogger = (namespace: string): Logger => {
  return new Logger(namespace);
};
